import { date } from '@orgazone/date-format';
import { INBOUND_MSG_TYPES, isInboundType, msgTypeLabel, type ApiMapping } from './api';

// LES RÈGLES DES MAPPINGS PARTAGÉS, CÔTÉ ÉCRAN — ce que la bibliothèque, la fiche d'un fournisseur, l'assistant et la
// fenêtre « Rattacher » affichent tous. Une seule écriture, pour qu'un mapping ne soit pas « en place » sur un écran
// et « manquant » sur un autre.
// ⚠️ « À REVOIR » SE JUGE PAR MAPPING : le serveur compte, pour chacun, les messages que SA traduction a bloqués
//    (`errorCount`). Deux mappings du même type chez un fournisseur — les deux catalogues de Bestseller — ne
//    s'allument donc plus ensemble, et un refus du back-office n'accuse pas un mapping qui a traduit proprement.

/** Ce qu'un mapping reconnaît et ce qu'il produit — les champs communs à la liste et à la fiche d'un fournisseur. */
export interface MappingFlow { sourceFormat: string; discriminant: string; canonicalType: string }

const FORMAT_LABEL: Record<string, string> = { edifact: 'EDIFACT', xml: 'XML', positional: 'Positionnel' };

/** Le flux reconnu, en une ligne : « XML « Invoice » ». */
export function flowLabel(m: MappingFlow): string {
  return `${FORMAT_LABEL[m.sourceFormat] ?? m.sourceFormat} « ${m.discriminant} »`;
}

/** Le flux et ce qu'il produit : « XML « Invoice » → INVOICE ». */
export function flowToLabel(m: MappingFlow): string {
  return `${flowLabel(m)} → ${m.canonicalType}`;
}

/** Deux mappings reconnaissent-ils le même fichier ? Un fournisseur n'en reçoit qu'un par flux (le serveur le refuse). */
export function sameFlow(a: MappingFlow, b: MappingFlow): boolean {
  return a.sourceFormat === b.sourceFormat && a.discriminant === b.discriminant;
}

/** Un compte de mappings, accordé. */
export function mappingCount(n: number): string {
  return `${n} mapping${n > 1 ? 's' : ''}`;
}

/** Un compte de fournisseurs, accordé — « aucun » quand le mapping ne traduit rien. */
export function supplierCount(n: number): string {
  return n === 0 ? 'aucun' : `${n} fournisseur${n > 1 ? 's' : ''}`;
}

/** Le flux lu dans un fichier reçu, tel que le journal le dit. ⚠️ Le positionnel est une PRÉSOMPTION (tout texte qui
 *  n'est ni EDIFACT ni XML est lu par son premier caractère, un CSV aussi) : on dit ce qui a été lu, pas ce qu'est le fichier. */
export function readFlowLabel(format: string, discriminant: string): string {
  return format === 'positional' ? `premier caractère « ${discriminant} »` : flowLabel({ sourceFormat: format, discriminant, canonicalType: '' });
}

/** Un mapping « à revoir » : des messages sont en erreur parce que SA traduction a bloqué. */
export function mappingInReview(m: { errorCount?: number }): boolean {
  return (m.errorCount ?? 0) > 0;
}

/**
 * Depuis quand la version en service traduit — « depuis le 03/11/2026 ». Un mapping qui n'a pas changé de version
 * depuis que la date est notée n'en a pas : l'écran dit alors le nombre de versions, jamais une date devinée.
 */
export function inServiceLabel(m: { currentSince: string | null; versionCount: number }): string {
  return m.currentSince ? `depuis le ${date(m.currentSince)}` : `sur ${m.versionCount} version${m.versionCount > 1 ? 's' : ''}`;
}

export type CoverageStatus = 'ok' | 'review' | 'missing';

/** L'état d'un type chez un fournisseur, d'après les mappings qui le lui traduisent. */
export function typeStatus(mappings: ReadonlyArray<{ errorCount?: number }>): CoverageStatus {
  return mappings.length === 0 ? 'missing' : mappings.some(mappingInReview) ? 'review' : 'ok';
}

/** Une ligne de couverture : un type ENTRANT, ses mappings rattachés, son état, et s'il est déclaré par le fournisseur. */
export interface CoverageRow<M extends MappingFlow> { type: string; mappings: M[]; status: CoverageStatus; declared: boolean }

/**
 * La couverture d'un fournisseur : une ligne par type entrant qu'il déclare OU qu'un de ses mappings produit, dans
 * l'ordre du référentiel.
 * ⚠️ 🔴 UN MAPPING RATTACHÉ NE DISPARAÎT JAMAIS DE LA FICHE : rattaché depuis le journal (tous types) ou resté après un
 *    type décoché, il traduit les fichiers du fournisseur — sa ligne le dit « non déclaré » plutôt que de le taire.
 * ⚠️ Les sortants n'y figurent pas : ils sont construits par la plateforme.
 */
export function supplierCoverage<M extends MappingFlow & { errorCount?: number }>(declared: readonly string[], attached: readonly M[]): CoverageRow<M>[] {
  return INBOUND_MSG_TYPES
    .filter((t) => declared.includes(t.key) || attached.some((m) => m.canonicalType === t.key))
    .map((t) => {
      const mappings = attached.filter((m) => m.canonicalType === t.key);
      return { type: t.key, mappings, status: typeStatus(mappings), declared: declared.includes(t.key) };
    });
}

/** Les types sortants déclarés par un fournisseur — nommés à part, sans mapping. */
export function outboundTypes(declared: readonly string[]): string[] {
  return declared.filter((k) => !isInboundType(k));
}

/** Un groupe de la bibliothèque : un type entrant et ses mappings, par nom. */
export interface LibraryGroup { type: string; label: string; mappings: ApiMapping[] }

/**
 * La bibliothèque filtrée et rangée par type : la recherche porte sur le nom, le flux et le type ; un filtre de types
 * vide les garde tous. Seuls les types qui gardent au moins un mapping ont un groupe.
 */
export function libraryGroups(mappings: readonly ApiMapping[], query: string, types: readonly string[]): LibraryGroup[] {
  const q = query.trim().toLowerCase();
  const kept = mappings.filter((m) => (types.length === 0 || types.includes(m.canonicalType))
    && (!q || `${m.name} ${flowLabel(m)} ${m.canonicalType} ${msgTypeLabel(m.canonicalType)}`.toLowerCase().includes(q)));
  return INBOUND_MSG_TYPES
    .map((t) => ({ type: t.key, label: t.label, mappings: kept.filter((m) => m.canonicalType === t.key).sort((a, b) => a.name.localeCompare(b.name, 'fr')) }))
    .filter((g) => g.mappings.length > 0);
}

/** Les mappings qu'on peut rattacher à ce fournisseur pour ce type : ceux qui le produisent et ne lui sont pas déjà rattachés. */
export function attachCandidates(mappings: readonly ApiMapping[], type: string, attachedIds: readonly string[]): ApiMapping[] {
  return mappings.filter((m) => m.canonicalType === type && !attachedIds.includes(m.id));
}

/** Ce qu'un fichier non reconnu permet de rattacher : les mappings qui reconnaissent SON flux, quel que soit leur type. */
export function flowCandidates(mappings: readonly ApiMapping[], flow: { format: string; discriminant: string }, attachedIds: readonly string[]): ApiMapping[] {
  return mappings.filter((m) => m.sourceFormat === flow.format && m.discriminant === flow.discriminant && !attachedIds.includes(m.id));
}

/**
 * Le mapping par lequel le fournisseur reçoit DÉJÀ le flux de ce candidat, s'il y en a un : le rattachement serait
 * refusé par le serveur, la fenêtre le dit avant.
 */
export function attachConflict<M extends MappingFlow & { id: string }>(candidate: MappingFlow & { id: string }, attached: readonly M[]): M | null {
  return attached.find((m) => m.id !== candidate.id && sameFlow(m, candidate)) ?? null;
}

/** Ce qu'un message du journal sait de son mapping : assez pour décider ce que son volet montre et propose. */
export interface MessageMappingFacts {
  direction: 'in' | 'out';
  status: string;
  supplierId: string | null;
  traceRecorded: boolean;
  mappingId: string | null;
  mappingName: string | null;
  mappingVersion: number | null;
  flowFormat: string | null;
  flowDiscriminant: string | null;
}

/**
 * Ce que le volet d'un message dit et propose de son mapping.
 * ⚠️ UN FICHIER NON RECONNU N'A PAS DE TYPE, IL A UN FLUX : c'est lui que le volet montre (« Flux lu »), et que
 *    « Rattacher un mapping » filtre. Un fichier où rien ne s'est lu (EDIFACT sans UNH, XML sans racine, vide, binaire)
 *    n'a rien à rattacher — aucun mapping ne pourrait le reconnaître : le bouton n'est pas offert, le motif réel est
 *    dans la conclusion (§23.0).
 * ⚠️ Un message traduit avant que le mapping soit noté n'en nomme aucun : on ne le devine pas d'après son type. Et un
 *    non reconnu reçu avant que le flux soit noté ne dit pas « aucun flux lisible » — il dit qu'on ne sait pas, et
 *    qu'un rejeu le lira.
 */
export function messageMappingView(m: MessageMappingFacts): {
  flow: { format: string; discriminant: string } | null;
  /** « Flux lu » — seulement pour un fichier non reconnu. */
  flowRead: string | null;
  /** « <mapping> · vN », sous l'étape « Traduit ». */
  translatedBy: string | null;
  canOpenMapping: boolean;
  canAttach: boolean;
} {
  const unrecognized = m.status === 'unrecognized';
  const flow = m.flowFormat && m.flowDiscriminant ? { format: m.flowFormat, discriminant: m.flowDiscriminant } : null;
  return {
    flow,
    flowRead: !unrecognized ? null
      : !m.traceRecorded ? 'non noté — rejouez le message pour le lire'
        : flow ? readFlowLabel(flow.format, flow.discriminant) : 'aucun flux lisible',
    translatedBy: m.mappingId && m.mappingName ? `${m.mappingName} · v${m.mappingVersion}` : null,
    canOpenMapping: !!m.mappingId,
    canAttach: m.direction === 'in' && unrecognized && !!m.supplierId && !!flow,
  };
}
